/**
 * Shared card primitives for the metric pages: Card, CardHead (with the ⓘ
 * Explain popup), Tile. Keeps every page consistent + themable via tokens.
 *
 * Tile takes an optional `drill` request — when present the tile is clickable
 * (↗ affordance + hover ring) and opens the full DrillExplorer. "Click through
 * just about every single card" is the product rule; a tile WITHOUT `drill`
 * should be the documented exception (pure derived/estimate numbers).
 */

import { createContext, forwardRef, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { filterClientExplainLines, cardExplainSourceByRenderId } from "@data-dashboard/shared";
import { useDrill, type DrillRequest } from "../lib/drill.js";
import { Sparkline } from "./Sparkline.js";
import { useCardChart, isChartableMetric } from "./CardTrend.js";
import { useCardName, CardAnchorCtx } from "../lib/card-anchor.js";
import { useExplainOverride, type ExplainOverride } from "../lib/card-approval.js";

/**
 * Card. When `onClick` is present the affordance is AUTOMATIC — pointer
 * cursor, hover ring, and an ↗ badge in the top-right corner (founder,
 * 2026-06-12: "all cards that are clickable need the little hand + arrow,
 * not just some"). Callers must not hand-roll className/title for this.
 * forwardRef so cards can be observed for lazy chart-loading.
 */
/**
 * Drill-affordance placement (founder 2026-07-06: "the arrow should be next to
 * the info icon; when it's on the right it clashes with stuff"). A clickable Card
 * advertises its drillability through CardDrillCtx; a CardHead inside it renders a
 * single ↗ right after the ⓘ and CLAIMS the affordance, which suppresses the
 * Card's own corner ↗. A clickable card that has no CardHead never claims, so it
 * keeps the corner ↗ as a fallback — no card silently loses its "click me" cue.
 */
const CardDrillCtx = createContext<{ drillable: boolean; claim: () => void }>({ drillable: false, claim: () => undefined });

/** The single drill ↗ shown right after the ⓘ on a clickable card's header. */
export function DrillArrow(): React.ReactElement {
  return <span title="Click to explore" aria-hidden="true" style={{ color: "var(--faint)", fontSize: 12, lineHeight: 1 }}>↗</span>;
}

export const Card = forwardRef<HTMLDivElement, { children: React.ReactNode; style?: React.CSSProperties; onClick?: () => void; className?: string; title?: string; arrow?: boolean; anchor?: string }>(
  function Card({ children, style, onClick, className, title, arrow = true, anchor }, ref): React.ReactElement {
  const clickable = onClick !== undefined;
  // The header (CardHead) moves the ↗ next to the ⓘ and claims it; until/unless
  // one does, we render the corner ↗ as a fallback so no clickable card loses its
  // cue. useLayoutEffect in CardHead claims before paint, so the corner never flashes.
  const [claimed, setClaimed] = useState(false);
  const ctx = useMemo(() => ({ drillable: clickable && arrow, claim: () => setClaimed(true) }), [clickable, arrow]);
  return (
    // data-card-anchor lets "See it live →" scroll to + flash this card (spotlight.tsx).
    <div ref={ref} onClick={onClick} data-card-anchor={anchor}
      className={clickable ? `dd-clickable-card ${className ?? ""}`.trim() : className}
      title={clickable ? title ?? "Click to explore" : title}
      style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-card)", boxShadow: "var(--shadow-card)", padding: "16px 18px", marginBottom: 16, ...(clickable ? { cursor: "pointer", position: "relative" } : {}), ...style }}>
      {clickable && arrow && !claimed && <span aria-hidden="true" style={{ position: "absolute", top: 14, right: 16, color: "var(--faint)", fontSize: 12, pointerEvents: "none" }}>↗</span>}
      <CardDrillCtx.Provider value={ctx}>{children}</CardDrillCtx.Provider>
    </div>
  );
});

export function CardHead({ title, explain, children, unfiltered }: { title: string; explain?: string[]; children?: React.ReactNode; unfiltered?: boolean }): React.ReactElement {
  const { drillable, claim } = useContext(CardDrillCtx);
  // Claim the drill ↗ before paint so the Card's corner arrow never flashes.
  useLayoutEffect(() => { if (drillable) claim(); }, [drillable, claim]);
  // The operator's rename (if any) for THIS card wins over the hardcoded title.
  // A dynamic suffix ("· Last 30 days") is kept only when there's no override.
  const shownTitle = useCardName(title);
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
      <span style={{ color: "var(--text)", fontSize: 12.5, fontWeight: 600, display: "flex", alignItems: "center", gap: 6, opacity: 0.85 }}>
        {shownTitle}
        {/* stopPropagation: the ⓘ must never ALSO fire the card's drill */}
        {explain && <span onClick={(e) => e.stopPropagation()}><Explain title={shownTitle} lines={explain} unfiltered={unfiltered} /></span>}
        {/* Drill ↗ sits right after the ⓘ (founder 2026-07-06), never in the corner. */}
        {drillable && <DrillArrow />}
      </span>
      {children && <div style={{ display: "flex", alignItems: "center", gap: 10 }} onClick={(e) => e.stopPropagation()}>{children}</div>}
    </div>
  );
}

export function Tile({ title, value, sub, explain, drill, delta, spark, preferSpark, headerAction, unfiltered }: {
  title: string;
  value: string;
  sub?: string;
  explain?: string[];
  /** Operator/admin surfaces pass true to keep the full explain (calc + example
   *  lines) — client tiles filter them out centrally (see Explain). */
  unfiltered?: boolean;
  drill?: DrillRequest;
  /** Period-over-period change as a fraction (0.24 = "24% higher") — renders
   *  the ▲/▼ pill (founder 2026-06-12: % comparisons almost everywhere). */
  delta?: number | null;
  /** Mini trendline under the number (real series only — never decorative). */
  spark?: number[];
  /** Render the passed `spark` INSTEAD of auto-charting the drill metric. For a
   *  tile whose headline isn't a single breakdown metric (e.g. Net adds =
   *  new − canceled): the caller supplies its own period-scoped series and it
   *  can also carry negatives, which the Sparkline handles but the auto-chart
   *  Spark does not. Suppresses the metric auto-chart fetch entirely. */
  preferSpark?: boolean;
  /** Optional control rendered in the top-right of the header, next to the chart
   *  menu (e.g. a number/table view toggle). Clicks don't open the drill. */
  headerAction?: React.ReactNode;
}): React.ReactElement {
  const { open } = useDrill();
  // Auto-chart: a Tile already knows its metric via `drill`, so it gets the same
  // chart-type dropdown + trend + rolling average as the KPI cards for free —
  // no per-card wiring (founder 2026-06-13: apply to ALL cards). Tiles whose
  // drill metric has no time series (or no drill) stay number-only.
  // Only auto-chart when the drill has NO filters — otherwise the (unfiltered)
  // breakdown trend wouldn't match this card's filtered headline number.
  // preferSpark opts out (the caller supplies its own period-scoped series).
  const trendMetric = !preferSpark && drill && !drill.filters && isChartableMetric(drill.metric) ? drill.metric : undefined;
  const chart = useCardChart({ metric: trendMetric, chartKey: `tile-${drill?.chartKey ?? drill?.metric ?? title}`, enabled: !!trendMetric });
  // Operator rename (if any) for this card wins over the hardcoded title.
  const shownTitle = useCardName(title);
  return (
    <Card ref={chart.ref} onClick={drill ? () => open(drill) : undefined} arrow={false} style={{ marginBottom: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{ color: "var(--text)", fontSize: 12.5, fontWeight: 600, display: "flex", alignItems: "center", gap: 6, opacity: 0.85 }}>
          {shownTitle}
          {/* stopPropagation: opening the ⓘ must not ALSO open the drill */}
          {explain && <span onClick={(e) => e.stopPropagation()}><Explain title={shownTitle} lines={explain} unfiltered={unfiltered} /></span>}
          {/* Drill ↗ beside the ⓘ (founder 2026-07-06), so it never crowds the chart menu. */}
          {drill && <DrillArrow />}
        </span>
        {(chart.menu || headerAction) && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }} onClick={(e) => e.stopPropagation()}>
            {headerAction}
            {chart.menu}
          </div>
        )}
      </div>
      <div className="dd-num" style={{ fontFamily: "var(--font-display)", fontSize: 27, fontWeight: 700, marginTop: 9, color: "var(--num)" }}>{value}</div>
      {(sub || (delta !== null && delta !== undefined)) && (
        <div style={{ display: "flex", gap: 9, marginTop: 6, alignItems: "center" }}>
          {delta !== null && delta !== undefined && (
            <span className="dd-num" style={{ fontSize: 12, fontWeight: 700, color: delta >= 0 ? "var(--good)" : "var(--bad)" }}>
              {delta >= 0 ? "▲" : "▼"} {Math.abs(delta * 100).toFixed(1)}%
            </span>
          )}
          {sub && <span style={{ color: "var(--body)", fontSize: 11.5 }}>{sub}</span>}
        </div>
      )}
      {/* Trend chart from the drill metric; else the legacy sparkline. */}
      {chart.body ? chart.body : spark && spark.length > 2 && (
        <div style={{ marginTop: 8 }}>
          <Sparkline data={spark} width={210} height={28} />
        </div>
      )}
    </Card>
  );
}

/**
 * The ⓘ explain popover — THE single place every card's explain array renders,
 * so the client-tooltip policy is applied here once for the whole app: lines
 * beginning "<b>How it's calculated:</b>" or "<b>Example…</b>" are dropped for
 * clients (founder 2026-07-12 — client tooltips keep only "What it is" + "Note").
 * `unfiltered` (operator/admin console surfaces) keeps every line. The card
 * definitions themselves are untouched — the full strings stay as the operator/
 * docs reference and the filter is one-line revertible.
 */
/** Replace the "What it is" / "Note" line with the operator's override text (or
 *  append it if that prefix isn't present), leaving every other line intact. */
export function applyExplainOverride(lines: string[], ov: ExplainOverride | undefined): string[] {
  if (!ov || (!ov.what && !ov.note)) return lines;
  const out = [...lines];
  const setLine = (prefix: string, text: string): void => {
    const line = `${prefix} ${text}`;
    const idx = out.findIndex((l) => l.startsWith(prefix));
    if (idx >= 0) out[idx] = line;
    else out.push(line);
  };
  if (ov.what) setLine("<b>What it is:</b>", ov.what);
  if (ov.note) setLine("<b>Note:</b>", ov.note);
  return out;
}

export function Explain({ title, lines, unfiltered }: { title: string; lines: string[]; unfiltered?: boolean }): React.ReactElement {
  // SINGLE SOURCE OF TRUTH (founder 2026-07-13: "variance between the live page
  // and what you're saying on each card"). The "What it is" / "Note" lines resolve
  // in strict precedence so the live ⓘ and the review row can never drift:
  //   operator override → catalog definition/note → the component's local line.
  // The catalog copy (by renderId, from CardAnchorCtx) is applied FIRST as the
  // base, so a catalog copy-edit changes the live card without touching this
  // component; when the catalog is silent for this renderId (a section-wrapper /
  // shared / interpolated card), the component's own line stays (the fallback).
  // How-it's-calculated / Example lines are always the component's (unfiltered
  // operator/demo surfaces still show them).
  const anchorId = useContext(CardAnchorCtx);
  const catalog = cardExplainSourceByRenderId(anchorId);
  const withCatalog = catalog
    ? applyExplainOverride(lines, { what: catalog.what, note: catalog.note })
    : lines;
  const override = useExplainOverride(anchorId);
  const visibleLines = applyExplainOverride(filterClientExplainLines(withCatalog, unfiltered), override);
  const [open, setOpen] = useState(false);
  const [hov, setHov] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const W = 300;

  // The popup is PORTALED to <body> with position:fixed and an opaque surface
  // so a neighbouring card's stacking context can never paint over it or show
  // through it (founder 2026-06-16: "the info box comes up behind, and it's too
  // see-through"). Same fix as ChartTypeMenu. Positioned from the ⓘ button rect.
  const place = (): void => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left, window.innerWidth - W - 8)) });
  };
  useEffect(() => {
    if (!open) return;
    place();
    const onDown = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (!btnRef.current?.contains(t) && !popRef.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") setOpen(false); };
    // Scroll/resize → REPOSITION to follow the ⓘ (never auto-close). Founder
    // 2026-07-07: "if I move after clicking, it goes away — it should stay until
    // I click out." Incidental reflows/scrolls (a re-render, a hover reflow, the
    // keep-previous-data layout settle) were closing it; now only a click-outside
    // or Escape dismisses it.
    const reposition = (): void => place();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  return (
    <span style={{ display: "inline-flex" }}>
      <button ref={btnRef} onClick={() => setOpen((o) => !o)} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        aria-label="Explain" aria-expanded={open} title="What's this?"
        style={{ border: "none", background: "transparent", color: open || hov ? "var(--accent)" : "var(--body)", cursor: "pointer", fontSize: 15, padding: 0, lineHeight: 1, display: "inline-flex", transition: "color .12s" }}>ⓘ</button>
      {open && pos && createPortal(
        <div ref={popRef} role="dialog" onClick={(e) => e.stopPropagation()}
          style={{ position: "fixed", top: pos.top, left: pos.left, width: W, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-md)", boxShadow: "var(--shadow-pop)", padding: 14, zIndex: 200 }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--text)", fontSize: 13, marginBottom: 7 }}>{title}</div>
          {visibleLines.map((l, i) => <div key={i} style={{ color: "var(--body)", fontSize: 12.5, lineHeight: 1.5, marginBottom: 5 }} dangerouslySetInnerHTML={{ __html: l }} />)}
        </div>,
        document.body,
      )}
    </span>
  );
}

// Re-exported from the shared formatter so existing `import { moneyFmt } from
// "./cards.js"` callers keep working (single source of truth lives in lib/format).
export { moneyFmt } from "../lib/format.js";

/**
 * Big bold RED "SAMPLE DATA" tag (founder 2026-06-15: "make any card using
 * sample data have a big bold red SAMPLE DATA tag" — was a small amber pill).
 * Cards whose data source isn't connected or whose inputs aren't entered yet
 * show representative numbers so the DESIGN can be reviewed; this tag keeps
 * them unmistakably honest. Always pair it with example values, never silence.
 */
export function SampleBadge(): React.ReactElement {
  return (
    <span title="Example numbers — not your data" style={{
      display: "inline-block", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.07em",
      color: "#fff", background: "var(--bad)", textTransform: "uppercase",
      borderRadius: 5, padding: "2px 8px", verticalAlign: "middle", whiteSpace: "nowrap",
    }}>Sample data</span>
  );
}
